import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, InlineLabeledCheckbox, TscCheckboxAlignment, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import {MinWidthButtonWrapper, StyledWarning} from '../util/StyledElements';
import '../../static/css/modal.less';
import styled from 'styled-components';
import captionApi from '../../service/captionApi';
import Constants from '../../constants/Constants';
import {SetupWorkerState} from '../../model/workerState';
import TranscriptionApi from '../../service/transcriptionApi';
import {CaptioningState, ICaptionData} from '../../model/captionModels';
import UserEmailInput from '../details/UserEmailInput';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
import {mediaStore, updateMediaState} from '../../context/mediaProvider';
import {captionDataStore, updateCaptionDataState} from '../../context/captionDataProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';

const Message = styled.div`
   margin-bottom: 1rem;
`;

const Options = styled.div`
   margin-top: 1rem;
   margin-bottom: 1rem;
   div label {
      font-weight: normal;
   }
`;

export const AddAssigneeModalBase: React.FC<IAddAssigneeModalProps & IStateMappedProps> = ({currentEmail, mediaId, onClose, theme, Hash, MediaId, captionData}) => {
   const {t} = useTranslation();
   const [email, setEmail] = useState(currentEmail || '');
   const [isValidEmail, setIsValidEmail] = useState(false);
   const [workerState, setWorkerState] = SetupWorkerState();
   const [isGeneratingCaptions, setIsGeneratingCaptions] = useState(false);
   const [errorText, setErrorText] = useState('');

   const updateWorkerState = (isWorking: boolean, error: string) => {
      setWorkerState({isWorking: isWorking, hasError: !!error});
      setErrorText(error);
   };

   const onSave = async () => {
      updateWorkerState(true, '');

      try {
         await captionApi.setAssignee(mediaId, email);

         if (isGeneratingCaptions) {
            try {
               await TranscriptionApi.startAutomatedTranscription(Hash);
               updateCaptionDataState(await captionApi.getStatus(MediaId));
            } catch {
               updateWorkerState(false, t('accessibility.assignee.modal.error'));
               return;
            }
         }

         updateMediaState({CaptioningAssignedToEmail: email});
      } catch (ex) {
         const errorMessage = ex.status === Constants.statusCodes.unprocessable ? 'accessibility.assignee.modal.userNotFoundError' : 'accessibility.assignee.modal.error';
         updateWorkerState(false, t(errorMessage));
         return;
      }

      updateWorkerState(false, '');
      onClose();
   };

   const onCheckboxClick = () => {
      setIsGeneratingCaptions(!isGeneratingCaptions);
   };

   const buttonGroup: React.JSX.Element = (
      <div className="button-group">
         <MinWidthButtonWrapper>
            <ControlButton
               label={t('general.cancel')}
               buttonType={TscButtonType.secondary}
               disabled={workerState.isWorking}
               themeName={theme}
               onClick={onClose}
               testId={'change-assignee-cancel-button'}
            />
            <ControlButton
               label={t('general.save')}
               buttonType={TscButtonType.primary}
               disabled={workerState.isWorking || !isValidEmail}
               themeName={theme}
               onClick={onSave}
               testId={'change-assignee-apply-button'}
            />
         </MinWidthButtonWrapper>
      </div>
   );

   const onEmailChange = (newEmail: string, isValid: boolean) => {
      setEmail(newEmail);
      setIsValidEmail(isValid);
      updateWorkerState(false, '');
   };

   const sharedId = 'assignee-modal-email';
   const captionTracksLength = captionData.filter(c => c.CaptioningState !== CaptioningState.None).length;
   const modalTitle = currentEmail ? t('accessibility.assignee.modal.modifyTitle') : t('accessibility.assignee.modal.title');

   return (
      <BasicModalPortal title={modalTitle} width={cssConstants.defaultModalWidth} onClose={onClose} themeName={theme} isLoading={workerState.isWorking}>
         <div className="modal-body">
            <div className="form-body themeable-section">
               <Message>{t('accessibility.assignee.modal.message')}</Message>
               <label htmlFor={sharedId}>{t('accessibility.assignee.modal.label')}</label>
               <UserEmailInput initialEmail={currentEmail} onEmailChange={onEmailChange} textInputId={sharedId} />
               <Options>
                  {!captionTracksLength && <InlineLabeledCheckbox labelText={t('accessibility.assignee.modal.generateText')} isChecked={isGeneratingCaptions} onClick={onCheckboxClick} isCentered={false} checkboxAlignment={TscCheckboxAlignment.left} />}
               </Options>
               {workerState.hasError && <StyledWarning id="error-message">{errorText}</StyledWarning>}
               {buttonGroup}
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface IAddAssigneeModalProps {
   currentEmail: string;
   mediaId: string;
   onClose(): void;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
   Hash: string;
   MediaId: string;
   captionData: ICaptionData[];
}

const mapStatesToProps = (
   {currentEmail, mediaId, onClose}: IAddAssigneeModalProps,
   {theme}: Partial<IStateMappedProps>,
   {Hash, MediaId}: Partial<IStateMappedProps>,
   {captionData}: Partial<IStateMappedProps>
): IStateMappedProps & IAddAssigneeModalProps => ({currentEmail, mediaId, onClose, theme, Hash, MediaId, captionData});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore, captionDataStore)(AddAssigneeModalBase);
